/**
 * Languages of the portal. Albanian is the default and lives at the root (/libra);
 * every other language gets its own prefix (/en/libra, /de/libra, ...).
 * Keep this list identical to the `i18n.locales` block in next.config.ts.
 */
export const locales = ['sq', 'en', 'de', 'es', 'tr', 'bs', 'ar']

export const defaultLocale = 'sq'

export type Locale = 'sq' | 'en' | 'de' | 'es' | 'tr' | 'bs' | 'ar'

interface LocaleMeta {
    /** The language's own name, shown in the language switcher. */
    nativeName: string
    /** How the language is written in `books.language` (the CMS stores Albanian names). */
    bookLanguage: string
    dir: 'ltr' | 'rtl'
    /** Open Graph locale. */
    ogLocale: string
}

export const localeMeta: Record<Locale, LocaleMeta> = {
    sq: { nativeName: 'Shqip', bookLanguage: 'Shqip', dir: 'ltr', ogLocale: 'sq_AL' },
    en: { nativeName: 'English', bookLanguage: 'Anglisht', dir: 'ltr', ogLocale: 'en_US' },
    de: { nativeName: 'Deutsch', bookLanguage: 'Gjermanisht', dir: 'ltr', ogLocale: 'de_DE' },
    es: { nativeName: 'Español', bookLanguage: 'Spanjisht', dir: 'ltr', ogLocale: 'es_ES' },
    tr: { nativeName: 'Türkçe', bookLanguage: 'Turqisht', dir: 'ltr', ogLocale: 'tr_TR' },
    bs: { nativeName: 'Bosanski', bookLanguage: 'Boshnjakisht', dir: 'ltr', ogLocale: 'bs_BA' },
    ar: { nativeName: 'العربية', bookLanguage: 'Arabisht', dir: 'rtl', ogLocale: 'ar_AR' }
}

export const isLocale = (value: unknown): value is Locale =>
    typeof value === 'string' && (locales as string[]).includes(value)

export const toLocale = (value: unknown): Locale => (isLocale(value) ? value : (defaultLocale as Locale))

/** The portal language that matches a book's `language` value, if any (e.g. Gjermanisht → de). */
export const localeForBookLanguage = (language: string | null | undefined): Locale | null => {
    const key = (language ?? '').trim().toLowerCase()
    const match = (Object.keys(localeMeta) as Locale[]).find(
        (code) => localeMeta[code].bookLanguage.toLowerCase() === key
    )
    return match ?? null
}

/** Prefixes a site path with the language, e.g. ('/libra', 'de') → '/de/libra'. Albanian stays unprefixed. */
export const localePath = (path: string, locale: string): string => {
    const clean = path.startsWith('/') ? path : `/${path}`
    if (!isLocale(locale) || locale === defaultLocale) return clean
    if (clean === '/') return `/${locale}`
    return `/${locale}${clean}`
}

/** Replaces `{name}` placeholders in a translated string. */
export const fill = (template: string, values: Record<string, string | number>): string =>
    template.replace(/\{(\w+)\}/g, (match, key: string) =>
        key in values ? String(values[key]) : match
    )
